import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Award, Calendar, Home, Sparkles, User, LogOut, Shield, Gift, Settings, 
  Star, Users, Menu, X, Wrench, DollarSign, Tag, Search, ShoppingCart, 
  Calculator, ChevronDown, FileText, Heart, MessageSquare, Mail, MapPin,
  Briefcase, BookOpen, HelpCircle, Bot, Send, Clock, CloudRain
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { axiosInstance } from "@/App";
import { useCart } from "@/context/CartContext";
import HomeSelector from "@/components/HomeSelector";

const Header = ({ user, onLogout, onShowAuth, onShowRegister, onHomeChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedHomeId, setSelectedHomeId] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const { itemCount } = useCart();

  const isActive = (path) => location.pathname === path;

  // Handle home selection change
  const handleHomeSelect = (homeId, homeData) => {
    setSelectedHomeId(homeId);
    if (onHomeChange) {
      onHomeChange(homeId, homeData);
    }
  };

  // Handle protected navigation - show auth modal if not logged in
  const handleProtectedNav = (path, options = {}) => {
    if (user) {
      navigate(path, options);
    } else {
      onShowRegister ? onShowRegister() : onShowAuth();
    }
  };

  // Track scroll for sticky CTA
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-orange-100 bg-white/90 backdrop-blur-lg">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Left Section: Logo + Nav */}
          <div className="flex items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 mr-6 group" data-testid="header-logo-link">
              <img
                src="/assets/oc-tasker-logo-orange.png"
                alt="OC Tasker Logo"
                width="72"
                height="72"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="h-14 w-14 sm:h-16 sm:w-16 object-contain logo-hover-spin drop-shadow-md transition-transform group-hover:scale-105"
              />
              <span className="text-2xl sm:text-3xl font-extrabold tracking-tight text-black leading-none">
                OC Tasker
              </span>
            </Link>

            {/* Main Navigation - Desktop */}
            <TooltipProvider delayDuration={120}>
            <nav className="hidden lg:flex items-center space-x-1">
            {/* 1. Home */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/">
                  <Button
                    variant={isActive("/") ? "default" : "ghost"}
                    className={isActive("/") ? "bg-orange-600 hover:bg-orange-700 text-white" : "text-black"}
                    data-testid="nav-home"
                  >
                    <Home className="w-4 h-4 mr-2 text-orange-500" />
                    Home
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent>Your personalized dashboard — rewards, upcoming services, and messages.</TooltipContent>
            </Tooltip>

            {/* 2. Free Estimate */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => navigate("/get-estimate")}
                  variant={isActive("/get-estimate") ? "default" : "ghost"}
                  className={isActive("/get-estimate") ? "bg-orange-600 hover:bg-orange-700 text-white" : "text-black"}
                  data-testid="nav-estimate"
                >
                  <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                  Free Estimate
                </Button>
              </TooltipTrigger>
              <TooltipContent>Tell us about your project and we'll send a price estimate — free, no obligation.</TooltipContent>
            </Tooltip>

            {/* 3. Services */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/services">
                  <Button
                    variant={isActive("/services") ? "default" : "ghost"}
                    className={isActive("/services") ? "bg-orange-600 hover:bg-orange-700 text-white" : "text-black"}
                    data-testid="nav-services"
                  >
                    <Sparkles className="w-4 h-4 mr-2 text-purple-500" />
                    Services
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent>Every service we offer — plumbing, electrical, drywall, painting, TV mounting, and more.</TooltipContent>
            </Tooltip>

            {/* 4. StormReady */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/services/flood-barrier-rental">
                  <Button
                    variant={isActive("/services/flood-barrier-rental") ? "default" : "ghost"}
                    className={isActive("/services/flood-barrier-rental") ? "bg-orange-600 hover:bg-orange-700 text-white" : "text-black"}
                    data-testid="nav-stormready"
                  >
                    <CloudRain className={`w-4 h-4 mr-2 ${isActive("/services/flood-barrier-rental") ? "text-white" : "text-sky-600"}`} />
                    StormReady™
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent>Flood barriers &amp; sandbags, delivered and installed. Pick-up extra.</TooltipContent>
            </Tooltip>

            {/* 5. Done Right Rewards */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={isActive("/loyalty") ? "default" : "ghost"}
                  className={isActive("/loyalty") ? "bg-orange-600 hover:bg-orange-700 text-white" : "text-black"}
                  data-testid="nav-rewards"
                  onClick={() => handleProtectedNav("/loyalty")}
                >
                  <Award className="w-4 h-4 mr-2 text-amber-500" />
                  Done Right Rewards
                </Button>
              </TooltipTrigger>
              <TooltipContent>Earn points on every dollar you spend. Redeem for discounts, priority scheduling, and VIP perks.</TooltipContent>
            </Tooltip>

            {/* 6. Cool Stuff Dropdown */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="text-black" data-testid="nav-cool-stuff">
                        <Gift className="w-4 h-4 mr-2 text-pink-500" />
                        Cool Stuff
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                <Link to="/rewards">
                  <DropdownMenuItem data-testid="menu-rewards-public" className="cursor-pointer">
                    <Award className="w-4 h-4 mr-2 text-amber-500" />
                    Done Right Rewards
                  </DropdownMenuItem>
                </Link>
                <Link to="/gift-cards">
                  <DropdownMenuItem data-testid="menu-gift-cards" className="cursor-pointer">
                    <Gift className="w-4 h-4 mr-2 text-pink-500" />
                    Gift Cards
                  </DropdownMenuItem>
                </Link>
                <Link to="/window-cleaning">
                  <DropdownMenuItem data-testid="menu-window-cleaning" className="cursor-pointer">
                    <Calculator className="w-4 h-4 mr-2 text-cyan-500" />
                    Window Cleaning Calculator
                  </DropdownMenuItem>
                </Link>
                <Link to="/maintenance-membership">
                  <DropdownMenuItem data-testid="menu-maintenance-calc" className="cursor-pointer">
                    <Calculator className="w-4 h-4 mr-2 text-green-500" />
                    Property Maint Calculator
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuItem 
                  onClick={() => handleProtectedNav("/refer")} 
                  data-testid="menu-refer"
                  className="cursor-pointer"
                >
                  <Users className="w-4 h-4 mr-2 text-blue-500" />
                  Refer a Friend
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleProtectedNav("/bookings")} 
                  data-testid="menu-bookings"
                  className="cursor-pointer"
                >
                  <Calendar className="w-4 h-4 mr-2 text-indigo-500" />
                  My Bookings
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleProtectedNav("/my-estimates")} 
                  data-testid="menu-estimates"
                  className="cursor-pointer"
                >
                  <FileText className="w-4 h-4 mr-2 text-teal-500" />
                  My Estimates
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <Link to="/property-health-check">
                  <DropdownMenuItem data-testid="menu-health-check" className="cursor-pointer">
                    <Shield className="w-4 h-4 mr-2 text-green-500" />
                    Health Check
                  </DropdownMenuItem>
                </Link>
                <Link to="/reviews">
                  <DropdownMenuItem data-testid="menu-reviews" className="cursor-pointer">
                    <Star className="w-4 h-4 mr-2 text-yellow-500" />
                    Reviews
                  </DropdownMenuItem>
                </Link>
                <Link to="/find-a-pro">
                  <DropdownMenuItem data-testid="menu-find-pro" className="cursor-pointer">
                    <Search className="w-4 h-4 mr-2 text-purple-500" />
                    Find a Pro
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator />
                <Link to="/service-areas">
                  <DropdownMenuItem data-testid="menu-service-areas" className="cursor-pointer">
                    <MapPin className="w-4 h-4 mr-2 text-red-500" />
                    Service Areas
                  </DropdownMenuItem>
                </Link>
                <Link to="/about">
                  <DropdownMenuItem data-testid="menu-about" className="cursor-pointer">
                    <Shield className="w-4 h-4 mr-2 text-orange-500" />
                    About OC Tasker
                  </DropdownMenuItem>
                </Link>
                <Link to="/blog">
                  <DropdownMenuItem data-testid="menu-blog" className="cursor-pointer">
                    <BookOpen className="w-4 h-4 mr-2 text-blue-500" />
                    Blog
                  </DropdownMenuItem>
                </Link>
                <Link to="/faq">
                  <DropdownMenuItem data-testid="menu-faq" className="cursor-pointer">
                    <HelpCircle className="w-4 h-4 mr-2 text-purple-500" />
                    FAQ
                  </DropdownMenuItem>
                </Link>
                <Link to="/careers">
                  <DropdownMenuItem data-testid="menu-careers" className="cursor-pointer">
                    <Briefcase className="w-4 h-4 mr-2 text-green-600" />
                    Careers
                  </DropdownMenuItem>
                </Link>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TooltipTrigger>
              <TooltipContent>Gift cards, calculators, bookings, referrals, and more handy tools.</TooltipContent>
            </Tooltip>

            {/* 7. Ask Chat — AI field assistant (techs & admins only) */}
            {user && (user.is_technician || user.is_admin) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => navigate("/field?tab=ai")}
                    variant="ghost"
                    className={`relative gap-2 ${isActive("/field") && window.location.search.includes("tab=ai") ? "bg-tenant-primary-soft text-tenant-primary font-semibold" : "text-black hover:bg-tenant-primary-soft"}`}
                    data-testid="nav-ask-chat"
                  >
                    <img loading="lazy" decoding="async" src="/icons/ask-chat-robot.png" alt="" className="w-6 h-7 object-contain" />
                    Ask Chat
                    <AskChatUnreadBadge />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Your in-app AI field assistant — ask questions, snap photos, talk to it.
                  Already knows the job you're on. A pulsing dot means your admin posted new
                  answers to the team library.
                </TooltipContent>
              </Tooltip>
            )}

            {/* 8. Clock In — personal time-clock shortcut (techs & admins only).
                /field renders the Field Dashboard with the punch-in controls. */}
            {user && (user.is_technician || user.is_admin) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => navigate("/field")}
                    variant="ghost"
                    className={`relative gap-2 ${isActive("/field") && !window.location.search.includes("tab=ai") ? "bg-tenant-primary-soft text-tenant-primary font-semibold" : "text-black hover:bg-tenant-primary-soft"}`}
                    data-testid="nav-clock-in"
                  >
                    <Clock className="w-5 h-5" />
                    Clock In
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Your personal time clock. Punch in for a job, switch between
                  jobs, or log non-billable work (shop, materials run, training).
                </TooltipContent>
              </Tooltip>
            )}
          </nav>
          </TooltipProvider>
          </div>

          {/* Right Side - Home Selector, Cart & Account */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            {/* Home Selector - Only show for logged in users */}
            {user && (
              <div className="hidden sm:block">
              <HomeSelector
                selectedHomeId={selectedHomeId}
                onHomeSelect={handleHomeSelect}
                variant="compact"
                showAddButton={false}
                className="mr-1"
              />
              </div>
            )}
            
            {/* Help Icons */}
            {showHelp && (
              <HelpSearchOverlay onClose={() => setShowHelp(false)} token={""} />
            )}
            {showChat && (
              <ChatOverlay onClose={() => setShowChat(false)} token={""} user={user} />
            )}
            <Button variant="ghost" className="hidden sm:inline-flex p-2 hover:bg-blue-50" onClick={() => setShowHelp(true)} data-testid="header-search" aria-label="Search help topics">
              <Search className="w-4 h-4 text-blue-500" />
            </Button>
            <Button variant="ghost" className="hidden sm:inline-flex p-2 hover:bg-purple-50" onClick={() => navigate("/help")} data-testid="header-help" aria-label="Open help center">
              <HelpCircle className="w-4 h-4 text-purple-500" />
            </Button>
            <Button variant="ghost" className="p-2 hover:bg-green-50" onClick={() => setShowChat(true)} data-testid="header-ai-chat" aria-label="Open AI chat assistant">
              <Bot className="w-4 h-4 text-green-500" />
            </Button>

            {/* Cart Icon - Colorful Orange */}
            <Button
              variant="ghost"
              onClick={() => navigate("/cart")}
              className="relative p-2 hover:bg-orange-50"
              data-testid="nav-cart"
              aria-label={itemCount > 0 ? `Shopping cart (${itemCount} items)` : "Shopping cart"}
            >
              <ShoppingCart className="w-5 h-5 text-orange-500" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {itemCount > 9 ? "9+" : itemCount}
                </span>
              )}
            </Button>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>

            {/* Account Dropdown - Colorful Teal */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="rounded-full p-2 hover:bg-teal-50 border border-teal-200" 
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-5 h-5 text-teal-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {/* User Info */}
                  <div className="px-3 py-2 border-b">
                    <p className="text-sm font-semibold text-black">{user.full_name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                    <div className="flex items-center mt-1 text-xs">
                      <Award className="w-3 h-3 text-orange-500 mr-1" />
                      <span className="font-medium text-orange-600">{user.loyalty_tier}</span>
                      <span className="mx-1">•</span>
                      <span className="text-gray-600">{user.loyalty_points} pts</span>
                    </div>
                  </div>
                  
                  {/* Profile & Messages */}
                  <Link to="/profile">
                    <DropdownMenuItem data-testid="menu-profile" className="cursor-pointer">
                      <User className="w-4 h-4 mr-2 text-gray-500" />
                      Profile
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/messages">
                    <DropdownMenuItem data-testid="menu-messages" className="cursor-pointer">
                      <MessageSquare className="w-4 h-4 mr-2 text-blue-500" />
                      Messages
                    </DropdownMenuItem>
                  </Link>
                  
                  {/* Field Dashboard — time clock + jobs (safe to show near customers) */}
                  {(user.is_technician || user.is_admin) && (
                    <>
                      <Link to="/field?tab=ai">
                        <DropdownMenuItem data-testid="menu-ask-chat" className="cursor-pointer">
                          <img loading="lazy" decoding="async" src="/icons/ask-chat-robot.png" alt="" className="w-5 h-6 object-contain mr-2" />
                          Ask Chat
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/field">
                        <DropdownMenuItem data-testid="menu-field-dashboard" className="cursor-pointer">
                          <Calculator className="w-4 h-4 mr-2 text-emerald-600" />
                          Field Dashboard
                        </DropdownMenuItem>
                      </Link>
                    </>
                  )}

                  {/* Employee Portal - Bonus Dashboard + Pay + Statements */}
                  <Link to="/employee-portal">
                    <DropdownMenuItem data-testid="menu-employee-portal" className="cursor-pointer">
                      <Calculator className="w-4 h-4 mr-2 text-orange-500" />
                      My Pay & Bonus
                    </DropdownMenuItem>
                  </Link>

                  {/* Admin Section */}
                  {user.is_admin && (
                    <>
                      <DropdownMenuSeparator />
                      <div className="px-2 py-1">
                        <p className="text-xs font-semibold text-gray-400 uppercase">Admin</p>
                      </div>
                      <Link to="/admin">
                        <DropdownMenuItem data-testid="menu-admin" className="cursor-pointer">
                          <Shield className="w-4 h-4 mr-2 text-red-500" />
                          Admin Dashboard
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/leads">
                        <DropdownMenuItem data-testid="menu-leads" className="cursor-pointer">
                          <Users className="w-4 h-4 mr-2 text-blue-500" />
                          Leads
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/communications">
                        <DropdownMenuItem data-testid="menu-communications" className="cursor-pointer">
                          <MessageSquare className="w-4 h-4 mr-2 text-green-500" />
                          Communications
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/settings">
                        <DropdownMenuItem data-testid="menu-settings" className="cursor-pointer">
                          <Settings className="w-4 h-4 mr-2 text-gray-500" />
                          App Settings
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/reviews">
                        <DropdownMenuItem data-testid="menu-admin-reviews" className="cursor-pointer">
                          <Star className="w-4 h-4 mr-2 text-yellow-500" />
                          Manage Reviews
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/payouts">
                        <DropdownMenuItem data-testid="menu-payouts" className="cursor-pointer">
                          <DollarSign className="w-4 h-4 mr-2 text-green-500" />
                          Partner Payouts
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/coupons">
                        <DropdownMenuItem data-testid="menu-coupons" className="cursor-pointer">
                          <Tag className="w-4 h-4 mr-2 text-purple-500" />
                          Coupons & Promos
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/bonus-calc">
                        <DropdownMenuItem data-testid="menu-bonus-calc" className="cursor-pointer">
                          <Calculator className="w-4 h-4 mr-2 text-orange-500" />
                          Bonus Calc
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/bonus-calc-v1-1-live">
                        <DropdownMenuItem data-testid="menu-bonus-calc-v1-1-live" className="cursor-pointer">
                          <Calculator className="w-4 h-4 mr-2 text-indigo-600" />
                          Bonus Calc V1.1
                          <span className="ml-auto text-[9px] uppercase tracking-wider text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded">New</span>
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/bonus-calc-v11">
                        <DropdownMenuItem data-testid="menu-bonus-calc-v11" className="cursor-pointer">
                          <Calculator className="w-4 h-4 mr-2 text-blue-500" />
                          Bonus Calc 1.1
                          <span className="ml-auto text-[9px] uppercase tracking-wider text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">Shadow</span>
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/bonus-calc-v2">
                        <DropdownMenuItem data-testid="menu-bonus-calc-v2" className="cursor-pointer">
                          <Calculator className="w-4 h-4 mr-2 text-amber-600" />
                          Bonus Calc V2
                          <span className="ml-auto text-[9px] uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Beta</span>
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/smart-estimates">
                        <DropdownMenuItem data-testid="menu-smart-estimates" className="cursor-pointer">
                          <Calculator className="w-4 h-4 mr-2 text-green-500" />
                          Smart Estimates
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/smart-estimator-v2">
                        <DropdownMenuItem data-testid="menu-smart-estimator-v2" className="cursor-pointer">
                          <Calculator className="w-4 h-4 mr-2 text-emerald-600" />
                          Smart Estimator V2
                          <span className="ml-auto text-[9px] uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Beta</span>
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/email-activity">
                        <DropdownMenuItem data-testid="menu-email-activity" className="cursor-pointer">
                          <Mail className="w-4 h-4 mr-2 text-blue-500" />
                          Email Activity
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/admin/guides">
                        <DropdownMenuItem data-testid="menu-guide-hub" className="cursor-pointer">
                          <BookOpen className="w-4 h-4 mr-2 text-teal-500" />
                          Guide Hub
                        </DropdownMenuItem>
                      </Link>
                    </>
                  )}

                  <DropdownMenuSeparator />
                  <Link to="/help">
                    <DropdownMenuItem data-testid="menu-help" className="cursor-pointer">
                      <HelpCircle className="w-4 h-4 mr-2 text-blue-500" />
                      Help Center
                    </DropdownMenuItem>
                  </Link>
                  {user?.is_admin && (
                    <Link to="/admin/todo">
                      <DropdownMenuItem data-testid="menu-todo" className="cursor-pointer">
                        <BookOpen className="w-4 h-4 mr-2 text-orange-500" />
                        Admin To-Do
                      </DropdownMenuItem>
                    </Link>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} data-testid="menu-logout" className="cursor-pointer text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={onShowAuth}
                className="bg-teal-600 hover:bg-teal-700 text-white px-3 sm:px-4"
                data-testid="login-button"
                aria-label="Sign In"
              >
                <User className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Sign In</span>
              </Button>
            )}
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-orange-100 py-4 space-y-2 max-h-[70vh] overflow-y-auto">
            {/* Home Selector in Mobile Menu */}
            {user && (
              <div className="px-3 py-2 mb-2 border-b border-orange-100">
                <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Active Property</p>
                <HomeSelector
                  selectedHomeId={selectedHomeId}
                  onHomeSelect={handleHomeSelect}
                  variant="full"
                  showAddButton={true}
                  label="Service Address"
                />
              </div>
            )}
            
            <Link to="/" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <Home className="w-4 h-4 mr-2 text-orange-500" />
                Home
              </Button>
            </Link>
            {user && (user.is_technician || user.is_admin) && (
              <Link to="/field?tab=ai" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start text-black" data-testid="mobile-nav-ask-chat">
                  <img loading="lazy" decoding="async" src="/icons/ask-chat-robot.png" alt="" className="w-5 h-6 mr-2 object-contain" />
                  Ask Chat
                </Button>
              </Link>
            )}
            <Button 
              variant="ghost" 
              className="w-full justify-start text-black"
              onClick={() => { navigate("/get-estimate"); setMobileMenuOpen(false); }}
            >
              <Calendar className="w-4 h-4 mr-2 text-blue-500" />
              Free Estimate
            </Button>
            <Link to="/services" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <Sparkles className="w-4 h-4 mr-2 text-purple-500" />
                Services
              </Button>
            </Link>
            <Link to="/services/flood-barrier-rental" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black" data-testid="mobile-nav-stormready">
                <CloudRain className="w-4 h-4 mr-2 text-sky-600" />
                StormReady™
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-black"
              onClick={() => { handleProtectedNav("/loyalty"); setMobileMenuOpen(false); }}
            >
              <Award className="w-4 h-4 mr-2 text-amber-500" />
              Done Right Rewards
            </Button>
            
            {/* Cool Stuff Section */}
            <div className="px-3 py-2">
              <p className="text-xs font-semibold text-gray-400 uppercase">Cool Stuff</p>
            </div>
            <Link to="/rewards" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black" data-testid="mobile-menu-rewards-public">
                <Award className="w-4 h-4 mr-2 text-amber-500" />
                Done Right Rewards
              </Button>
            </Link>
            <Link to="/gift-cards" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <Gift className="w-4 h-4 mr-2 text-pink-500" />
                Gift Cards
              </Button>
            </Link>
            <Link to="/window-cleaning" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <Calculator className="w-4 h-4 mr-2 text-cyan-500" />
                Window Cleaning Calculator
              </Button>
            </Link>
            <Link to="/maintenance-membership" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <Calculator className="w-4 h-4 mr-2 text-green-500" />
                Property Maint Calculator
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-black"
              onClick={() => { handleProtectedNav("/refer"); setMobileMenuOpen(false); }}
            >
              <Users className="w-4 h-4 mr-2 text-blue-500" />
              Refer a Friend
            </Button>
            {user && (
              <>
                <Link to="/bookings" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-black">
                    <Calendar className="w-4 h-4 mr-2 text-indigo-500" />
                    My Bookings
                  </Button>
                </Link>
                <Link to="/my-estimates" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-black">
                    <FileText className="w-4 h-4 mr-2 text-teal-500" />
                    My Estimates
                  </Button>
                </Link>
              </>
            )}
            <Link to="/property-health-check" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <Shield className="w-4 h-4 mr-2 text-green-500" />
                Health Check
              </Button>
            </Link>
            <Link to="/reviews" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <Star className="w-4 h-4 mr-2 text-yellow-500" />
                Reviews
              </Button>
            </Link>
            <Link to="/find-a-pro" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <Search className="w-4 h-4 mr-2 text-purple-500" />
                Find a Pro
              </Button>
            </Link>
            <Link to="/service-areas" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <MapPin className="w-4 h-4 mr-2 text-red-500" />
                Service Areas
              </Button>
            </Link>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <Shield className="w-4 h-4 mr-2 text-orange-500" />
                About OC Tasker
              </Button>
            </Link>
            <Link to="/blog" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <BookOpen className="w-4 h-4 mr-2 text-blue-500" />
                Blog
              </Button>
            </Link>
            <Link to="/faq" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black" data-testid="mobile-menu-faq">
                <HelpCircle className="w-4 h-4 mr-2 text-purple-500" />
                FAQ
              </Button>
            </Link>
            <Link to="/careers" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <Briefcase className="w-4 h-4 mr-2 text-green-600" />
                Careers
              </Button>
            </Link>
            
            {/* Cart in Mobile */}
            <Link to="/cart" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-black">
                <ShoppingCart className="w-4 h-4 mr-2 text-orange-500" />
                Cart {itemCount > 0 && `(${itemCount})`}
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Sticky Mobile CTA - Shows on scroll */}
      {isScrolled && (
        <div className="fixed bottom-0 left-0 right-0 z-50 sm:hidden bg-white border-t border-gray-200 p-3 shadow-lg safe-area-bottom">
          <Button
            onClick={() => navigate("/get-estimate")}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-4 text-base min-h-[56px]"
            data-testid="sticky-cta-mobile"
          >
            Get My Free Estimate
          </Button>
        </div>
      )}

      {/* Spacer for sticky CTA on mobile */}
      <div className="sm:hidden h-[72px]"></div>
    </header>
  );
};

export default Header;

// --------------------------------------------------------------------------
// AskChatUnreadBadge — subtle pulsing dot on the Ask Chat nav item whenever
// the Team Library has featured entries the user hasn't seen yet.
// Persists "last seen" timestamp in localStorage so techs who open the library
// clear their own badge. Also broadcasts a window event so multiple tabs sync.
// --------------------------------------------------------------------------
function AskChatUnreadBadge() {
  const [count, setCount] = useState(0);

  const load = async () => {
    try {
      const res = await axiosInstance.get("/tech-assistant/admin/repository");
      const entries = res.data?.entries || [];
      const lastSeenISO = localStorage.getItem("ask_chat_library_last_seen") || "";
      const unread = entries.filter((e) => !lastSeenISO || (e.asked_at && e.asked_at > lastSeenISO)).length;
      setCount(unread);
    } catch (_) { /* silent — nav badge is cosmetic */ }
  };

  useEffect(() => {
    load();
    // Re-poll every 5 minutes while the tab is open
    const id = setInterval(load, 5 * 60 * 1000);
    // Listen for same-tab updates (fired when user visits the library)
    const onSeen = () => load();
    window.addEventListener("ask-chat-library-seen", onSeen);
    return () => { clearInterval(id); window.removeEventListener("ask-chat-library-seen", onSeen); };
  }, []);

  if (!count) return null;
  return (
    <span
      className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white bg-red-500 shadow ring-2 ring-white"
      data-testid="ask-chat-unread-badge"
      aria-label={`${count} new Team Library ${count === 1 ? "entry" : "entries"}`}
    >
      <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-75" />
      <span className="relative">{count > 9 ? "9+" : count}</span>
    </span>
  );
}

// Help Search Overlay
function HelpSearchOverlay({ onClose, token }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const role = user.is_admin ? "admin" : "customer";
  const API = process.env.REACT_APP_BACKEND_URL + "/api";

  const doSearch = async () => {
    if (!search.trim()) return;
    const res = await fetch(`${API}/help/articles?role=${role}&search=${encodeURIComponent(search)}`);
    if (res.ok) setResults((await res.json()).articles || []);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-center pt-20" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="help-search-overlay">
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input autoFocus className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Search help articles..." value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === "Enter" && doSearch()} />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {results.length === 0 && search && <p className="text-sm text-center text-muted-foreground p-4">No results. Try different words.</p>}
          {results.map(a => (
            <a key={a.id} href="/help" className="block p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
              <div className="font-medium text-sm">{a.title}</div>
              <div className="text-xs text-muted-foreground line-clamp-1">{a.body}</div>
            </a>
          ))}
          {!search && <p className="text-sm text-center text-muted-foreground p-4">Type to search for help</p>}
        </div>
      </div>
    </div>
  );
}

// AI Chat Overlay
function ChatOverlay({ onClose, token, user }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I am the OC Tasker Help Assistant. Ask me anything!" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2));
  const API = process.env.REACT_APP_BACKEND_URL + "/api";
  const role = user?.is_admin ? "admin" : "customer";

  const send = async () => {
    if (!input.trim()) return;
    const q = input.trim();
    setMessages(prev => [...prev, { role: "user", text: q }]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/help/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q, role, session_id: sessionId }),
      });
      if (res.ok) {
        const d = await res.json();
        setMessages(prev => [...prev, { role: "assistant", text: d.answer }]);
      }
    } catch (e) { setMessages(prev => [...prev, { role: "assistant", text: "Sorry, try again." }]); }
    setLoading(false);
  };

  return (
    <div className="fixed bottom-4 right-4 z-[100] w-80 bg-white rounded-xl shadow-2xl border overflow-hidden" data-testid="chat-overlay">
      <div className="bg-orange-500 text-white p-3 flex items-center justify-between">
        <div className="flex items-center gap-2"><Bot className="w-5 h-5" /><span className="font-medium text-sm">AI Help Assistant</span></div>
        <button onClick={onClose} className="hover:bg-orange-600 rounded p-1"><X className="w-4 h-4" /></button>
      </div>
      <div className="h-64 overflow-y-auto p-3 space-y-2">
        {messages.map((m, i) => (
          <div key={`chat-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] p-2 rounded-lg text-xs ${m.role === "user" ? "bg-orange-500 text-white" : "bg-gray-100"}`}>{m.text}</div>
          </div>
        ))}
        {loading && <div className="text-xs text-gray-400">Thinking...</div>}
      </div>
      <div className="p-2 border-t flex gap-1">
        <input className="flex-1 text-sm border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-500"
          value={input} onChange={e => setInput(e.target.value)} placeholder="Ask anything..."
          onKeyDown={e => e.key === "Enter" && send()} />
        <button onClick={send} disabled={loading} className="bg-orange-500 text-white p-1.5 rounded hover:bg-orange-600"><Send className="w-3 h-3" /></button>
      </div>
    </div>
  );
}
